import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { ExtractedImage } from './useRecommendationDetection';
import { generatePrivateImageSimple } from '@/utils/generate-private-image';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';

interface AgentChatImageParams {
  image: ExtractedImage;
  messageId: string;
  // workbookId and goalThreadId removed - no longer required for private images
}

/**
 * Validate and fix image dimensions for Runware API compatibility
 * Runware requires dimensions to be multiples of 64, between 128-2048
 */
const validateAndFixDimensions = (width: string, height: string): { width: string; height: string } => {
  const roundToMultipleOf64 = (value: number): number => {
    // Ensure within bounds (128-1024 for optimal performance)
    const clampedValue = Math.max(128, Math.min(1024, value));
    // Round to nearest multiple of 64
    return Math.round(clampedValue / 64) * 64;
  };

  const widthNum = parseInt(width);
  const heightNum = parseInt(height);
  
  // Validate that we have valid numbers
  if (isNaN(widthNum) || isNaN(heightNum)) {
    console.warn('[ImageValidation] Invalid dimensions provided, using defaults:', { width, height });
    return { width: '512', height: '512' };
  }

  const validatedWidth = roundToMultipleOf64(widthNum);
  const validatedHeight = roundToMultipleOf64(heightNum);
  
  console.log('[ImageValidation] Dimension validation:', {
    original: { width: widthNum, height: heightNum },
    validated: { width: validatedWidth, height: validatedHeight },
    changed: validatedWidth !== widthNum || validatedHeight !== heightNum
  });

  return {
    width: validatedWidth.toString(),
    height: validatedHeight.toString()
  };
};

/**
 * Hook for generating images from agent chat recommendations
 * Now routes to private image library instead of project-based images
 * Simplified flow - creates private images that can be assigned to projects later
 */
export const useAgentChatImageGeneration = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { executeAIOperation } = useAIOperationWrapper();

  return useMutation({
    mutationFn: async ({ image, messageId }: AgentChatImageParams) => {
      console.log('[AgentChatImage] Starting private image generation for:', image.description);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Parse dimensions from size format (e.g., "1024x768")
      const [rawWidth, rawHeight] = image.size.split('x').map(dim => dim.trim());
      
      // Validate and fix dimensions for Runware API compatibility
      const { width, height } = validateAndFixDimensions(rawWidth, rawHeight);
      
      // Map style to format expected by the utility
      const styleForApi = image.style.replace('style-', '').toLowerCase(); // Remove any prefix and lowercase
      
      console.log('[AgentChatImage] Generation parameters:', {
        description: image.description,
        style: styleForApi,
        originalDimensions: { width: rawWidth, height: rawHeight },
        validatedDimensions: { width, height }
      });

      // Use the new unified image generation utility
      const result = await generatePrivateImageSimple({
        description: image.description,
        style: styleForApi,
        size: `${width}x${height}`,
        userId: user.id,
        executeAIOperation
      });
      
      console.log('[AgentChatImage] Image generated and saved successfully:', result.savedImage.id);
      
      // Return the generated image data with Supabase storage URL
      return {
        imageUrl: result.imageUrl, // Supabase storage URL
        description: image.description,
        style: image.style,
        size: `${width}x${height}`, // Return validated dimensions
        savedImageId: result.savedImage.id
      };
    },
    onSuccess: (data, variables) => {
      console.log('[AgentChatImage] Success, invalidating private image queries');
      
      // Invalidate private image generator queries (for Private Image Generator page)
      queryClient.invalidateQueries({ 
        queryKey: ['private-image-generator'],
        exact: false 
      });
      
      queryClient.invalidateQueries({ 
        queryKey: ['private-image-generator-count'],
        exact: false 
      });
      
      // Invalidate private image library queries (for Project Image Library)
      queryClient.invalidateQueries({ 
        queryKey: ['private-image-library'],
        exact: false 
      });
      
      // Invalidate workbook images queries (comprehensive coverage)
      queryClient.invalidateQueries({ 
        queryKey: ['workbook-images'],
        exact: false 
      });

      // Invalidate universal storage queries (for private_image content type)
      queryClient.invalidateQueries({ 
        queryKey: ['universal-storage-items'],
        exact: false 
      });
      
      queryClient.invalidateQueries({ 
        queryKey: ['universal-library-data'],
        exact: false 
      });
    },
    onError: (error) => {
      console.error('[AgentChatImage] Error:', error);
    },
  });
}; 